import { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Container, Nav, Navbar } from "react-bootstrap";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Button from "../common/components/Button";
import Clock from "../common/components/Clock";
import { Col, Row } from "../common/components/Grid";
import Icon from "../common/components/Icon";
import { useNetworkState } from "../common/hooks/appStateHook";
import useClientConfig from "../common/hooks/clientConfigHook";
import useCredentials from "../common/hooks/credentialsHook";
import { ConfirmDialog, Modal, ReactNodeDialog } from "../common/utils/dialogUtlities";
import UserSettingsPanel from "./settings/UserSettingsPanel";
import { AlertNav } from "./AlertInfo";
import WeatherInfo from "./WeatherInfo";
import { NetworkStatusIndicator } from "../common/components/NetworkStatusIndicator";
import { LanguageButton } from "../common/components/LanguageButton";
import useWebsocket from "../common/hooks/websocketHook";
import DefaultButton from "../common/components/DefaultButton";
import PositionInput from "./PositionInput";
import { useBroadcast } from "../common/hooks/broadcastHook";

const PublicNavBarContext = createContext({ setTitle: () => { }, setHideNav: () => { }, placeControls: () => { } });

export const usePublicNavBar = () => useContext(PublicNavBarContext);

export const PublicNavBar = ({ hideNav: inputHideNav, title: inputTitle, children }) => {
    const clientConfig = useClientConfig();
    const [searchParams] = useSearchParams();
    const [title, setTitle] = useState(inputTitle);
    const [hideNav, setHideNav] = useState(inputHideNav || searchParams.has('hideNav'));
    const controlRef = useRef();
    const placeControls = useCallback((controls) => {
        if (controlRef.current) {
            return createPortal(controls, controlRef.current)
        }
        return null;
    }, []);
    const visible = !hideNav;

    const context = useMemo(() => ({ title, setTitle, hideNav, setHideNav, placeControls }), [title, hideNav, placeControls]);
    return <div className="d-flex flex-column" style={{ height: 'calc(100vh)', backgroundColor: '#186ADB' }}>
        {visible && <Row className="px-4" style={{ backgroundColor: '#00aeef', color: '#ffffff' }}>
            <Col className="d-flex flex-column justify-content-center px-2" xs="auto" style={{ backgroundColor: 'white' }}>
                <img src="/eventLogo" alt="" style={{ display: 'block', maxHeight: '40px', maxWidth: '120px', height: 'auto', width: 'auto' }}></img>
                <small className="pt-1" style={{ color: '#00aeef' }}>for {clientConfig?.eventName}</small>
            </Col>
            <Col className="py-3 px-2" xs>
                <h4>{title}</h4>
            </Col>
            <div className="align-items-center"><LanguageButton></LanguageButton></div>
            <Col className="d-flex flex-column justify-content-center" xs="auto">
                <div ref={controlRef}></div>
            </Col>
        </Row>}
        <div style={{ flex: 1, overflowY: 'auto' }}>
            <PublicNavBarContext.Provider value={context}>
                {children || <Outlet></Outlet>}
            </PublicNavBarContext.Provider>
        </div>
    </div>;
}

const itemStyle = { color: 'white' };
const activeItem = { backgroundColor: 'white', color: '#0AAEEF' }

const NavBarTemplate = ({ links, controls, children }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const credentials = useCredentials();

    return <div className="d-flex flex-column" style={{ height: 'calc(100vh)' }}>
        <Navbar collapseOnSelect expand="md" variant="dark" className="chronobar py-0 px-0">
            <Container fluid className="px-0 d-flex h-100">
                <Navbar.Brand className="p-0 m-0" href="/">
                    <Row>
                        <Col className="py-1 px-2" xs="auto">
                            <img alt={t('logoChronicler')} src="/images/chronicler_sm.png"></img>
                            <small className="pt-1" style={{ color: 'white' }}>{t('forEvent', { name: clientConfig?.eventName })}</small>
                        </Col>
                        <Col xs="auto">
                            <NetworkStatusIndicator></NetworkStatusIndicator>
                        </Col>
                    </Row>
                </Navbar.Brand>
                <Navbar.Toggle className="mx-2 border-0" aria-controls="responsive-navbar-nav" style={{ color: 'white' }} />
                <Navbar.Collapse id="responsive-navbar-nav" className="d-md-flex align-items-md-stretch h-100">
                    {links && <Nav className="me-auto px-2 py-0 d-md-flex align-items-md-stretch">{links}</Nav>}
                    <Nav className="mx-auto d-none d-lg-flex justify-content-center flex-lg-grow-1">
                        <span className="bigClock"><Clock showDate={true} showZone={true}></Clock></span>
                    </Nav>
                    <Nav className="ms-auto justify-content-end align-items-md-stretch flex-grow-1 flex-lg-grow-0">
                        {controls}
                        <div className="d-none d-md-flex align-items-center px-2" style={{ backgroundColor: 'white' }}>
                            <div className="d-flex flex-column align-items-end">
                                <img src="/eventLogo" alt={t('logoClient', { name: clientConfig?.eventName })} style={{ display: 'block', maxHeight: '36px', maxWidth: '120px', height: 'auto', width: 'auto' }} />
                                <div className="text-end" style={{ fontSize: 'smaller', color: '#0AAEEF' }}>{credentials.username}</div>
                            </div>
                        </div>
                    </Nav>
                </Navbar.Collapse>
            </Container>
        </Navbar>
        <div>
        </div>
        <div style={{ flex: 1 }}>{children}</div>
    </div>
}

const CrewSOSDialog = ({ crew, onClose }) => {
    const { t } = useTranslation();
    const broadcast = useBroadcast();
    const style = useMemo(() => ({ backgroundColor: 'red', color: 'white', padding: '8px' }), []);
    return crew && <Modal show={true} backgdrop="static" style={{ borderRadius: '4px' }}>
        <Modal.Title style={style}>
            {t('crew_one')} {crew.displayid} {t('SOS')}
        </Modal.Title>
        <Modal.Body style={style}>
            <p>{t('crewSOSAlert', { callsign: crew.displayid })}</p>
            { (crew.w3w || (crew.latitude && crew.longitude)) ? <PositionInput position={crew} hideLabel readonly></PositionInput> : <p>{t('unknown')}</p> }
        </Modal.Body>
        <Modal.Footer style={style}>
            { crew.latitude && crew.longitude && <DefaultButton kind="marker" onClick={() => {
                broadcast('locate', { latitude: crew.latitude, longitude: crew.longitude });
                onClose();
            }} title={t('showMap')}></DefaultButton> }
            <DefaultButton kind="close" onClick={() => onClose()}></DefaultButton>
        </Modal.Footer>
    </Modal>
}


export const InternalNavBar = ({ children }) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    const credentials = useCredentials();
    const location = useLocation();
    const navigate = useNavigate();
    const { isOnline, wsStatus } = useNetworkState();
    const { register } = useWebsocket();
    const [showSettings, setShowSettings] = useState(false);
    const [showOfflineLogout, setShowOfflineLogout] = useState(false);
    const [sosCrew, setSosCrew] = useState();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.has('token') || params.has('refreshToken')) {
            params.delete('token');
            params.delete('refreshToken');
    
            const newSearch = params.toString();
            const newUrl =
                window.location.pathname +
                (newSearch ? `?${newSearch}` : '') +
                window.location.hash;
    
            // Replace current entry without reloading:
            window.history.replaceState({}, '', newUrl);
        }
    }, []);

    useEffect(() => {
        if (clientConfig.enableCrews && !clientConfig.hidecrews) {
            const cleanup = register('crewSOS', data => {
                setSosCrew(data);
            });
            return () => cleanup();
        }
    }, [clientConfig.enableCrews, clientConfig.hidecrews, register]);

    const doSignout = useCallback(() => {
        if ((!isOnline || wsStatus !== 'authenticated') && clientConfig.enableOfflineMode) {
            setShowOfflineLogout(true);
        } else {
            startTransition(() => navigate('/logout'));
        }
    }, [navigate, isOnline, wsStatus, clientConfig.enableOfflineMode]);

    return <>
        { sosCrew && <CrewSOSDialog crew={sosCrew} onClose={() => setSosCrew()}></CrewSOSDialog>}
        <ConfirmDialog showModal={showOfflineLogout} onClose={result => { if (result) { startTransition(() => navigate('/logout')); } else { setShowOfflineLogout(false) } }} title={t('offlineLogoutTitle')} message={t('offlineLogoutMessage')}></ConfirmDialog>
        <ReactNodeDialog showModal={showSettings} title={t('settings')} size="xlg" onClose={() => setShowSettings(false)}><UserSettingsPanel></UserSettingsPanel></ReactNodeDialog>
        <NavBarTemplate links={
            <>
                <Nav.Link as={Link} to="/cad" className="d-md-flex align-items-md-center px-1" style={location.pathname === '/cad' ? activeItem : itemStyle}><div>{t('cad')}</div></Nav.Link>
                <Nav.Link as={Link} to="/stats" className="d-md-flex align-items-md-center px-1" style={location.pathname === '/stats' ? activeItem : itemStyle}>{t('stats')}</Nav.Link>
                { clientConfig.enableUnity && <Nav.Link as={Link} to="/unity" className="d-md-flex align-items-md-center px-1" style={location.pathname === '/unity' ? activeItem : itemStyle}>{t('unity')}</Nav.Link> }
                { credentials.strict.hasFull('administrator') && <Nav.Link as={Link} to="/settings" className="d-md-flex align-items-md-center px-1" style={location.pathname === '/settings' ? activeItem : itemStyle}>{t('admin')}</Nav.Link> }
                { clientConfig.sitrep?.enable && <Nav.Link as={Link} to="/sitrep" className="d-md-flex align-items-md-center px-1" style={location.pathname === '/sitrep' ? activeItem : itemStyle}>{t('sitrep')}</Nav.Link> }
            </>
        } controls={
            <>
                { clientConfig.enableWeather && <div className="d-none d-md-flex align-items-center"><WeatherInfo></WeatherInfo></div> }
                <div className="align-items-center"><LanguageButton></LanguageButton></div>
                <div className="align-items-center"><AlertNav></AlertNav></div>
                <div className="align-items-center">
                    <Button className="h-100 border-0 px-2" title={t('settings')} style={{ color: 'white', backgroundColor: 'transparent' }} onClick={() => setShowSettings(true) }><Icon variant="solid" kind="gear"></Icon> <span className="d-inline d-md-none">{t('settings')}</span></Button>
                </div>
                <div className="align-items-center">
                    <Button className="h-100 border-0 px-2" title={t('signOut')} style={{ color: 'white', backgroundColor: 'transparent' }} onClick={doSignout}><Icon variant="solid" kind="right-from-bracket"></Icon> <span className="d-inline d-md-none">{t('signOut')}</span></Button>
                </div>
            </>
        }>
            {children}
        </NavBarTemplate>
    </>
}

export const SigninNavBar = () => {
    return <div className="h-100 w-100" style={{ backgroundColor: '#186ADB' }}>
        <NavBarTemplate controls={
            <div className="align-items-center"><LanguageButton></LanguageButton></div>
        }><Outlet></Outlet></NavBarTemplate>
    </div>
}